import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { timeout } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { MobileService } from "./mobile.service";

/**
 * FLIP-584. Whether account creation is offered from where this browser is (the API applies the same
 * rule as the 403 on Register / SelfRegister, using the country the edge stamps on the request). Used to
 * hide "Create one" on login and to keep /welcome and /register closed, so a visitor outside the allowed
 * countries is never walked through the sign-up form only to be refused at the end. Login is never asked.
 *
 * Fails open, like the edge rule: a failed or slow call (3 s, so it never holds a visitor's first page) or an
 * unknown answer counts as allowed, and a failure is not cached so the next page asks again. Concurrent
 * callers share one request. A plain HttpClient call rather than the NSwag client, which is regenerated from
 * the API's spec.
 */
@Injectable({ providedIn: "root" })
export class RegionService {
  private static readonly TIMEOUT_MS = 3000;

  private cached?: boolean;
  private pending?: Promise<boolean>;

  constructor(
    private http: HttpClient,
    private mobileService: MobileService,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  async registrationAllowed(): Promise<boolean> {
    // The mobile app is always allowed (the app stores limit it by country), so skip the call.
    if (this.mobileService.isMobileApp) {
      return true;
    }
    if (this.cached !== undefined) {
      return this.cached;
    }
    if (!this.pending) {
      this.pending = this.ask().then((allowed) => {
        this.pending = undefined;
        return allowed;
      });
    }
    return this.pending;
  }

  /** Never rejects: any failure, timeout or unintelligible answer is "allowed". */
  private async ask(): Promise<boolean> {
    try {
      const result = await this.http
        .get<{ allowed?: boolean }>(`${this.apiBaseUrl}/api/Account/RegistrationAllowed`)
        .pipe(timeout(RegionService.TIMEOUT_MS))
        .toPromise();
      if (typeof result?.allowed !== "boolean") {
        return true;
      }
      this.cached = result.allowed;
      return this.cached;
    } catch {
      return true;
    }
  }
}
